// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import { useTheme } from '@mui/material/styles'
import CardHeader from '@mui/material/CardHeader'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import SearchIcon from '@mui/icons-material/Search'

import * as XLSX from 'xlsx'

// ** Icons Imports
import DotsVertical from 'mdi-material-ui/DotsVertical'

// ** Third Party Imports
import { ApexOptions } from 'apexcharts'

// ** Custom Components Imports
import ReactApexcharts from 'src/@core/components/react-apexcharts'
import { useTranslation } from 'react-i18next'
import { salesData } from './StatisticsCard'
import {
  CardActionArea,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Grid,
  InputBase,
  Paper,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  TextField,
  Tooltip
} from '@mui/material'
import ChatClaude from '@/layouts/components/vertical/ChatClaude'
import React from 'react'
import {
  ContentCopy,
  Download,
  HdmiPort,
  PlusCircleOutline,
  SwapHorizontal
} from 'mdi-material-ui'
import { Science } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import Dialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import {
  containsChinese,
  deduplicateArrays,
  formatDate,
  formatVariantInheritance,
  getArr,
  groupObjectBySize,
  isWindow,
  processGeneHpoData,
  unique
} from '@/@core/utils/utils'
import {
  GetProp,
  message,
  Table,
  TablePaginationConfig,
  TableProps
} from 'antd'
import {
  ChatClaudeDto,
  chatQwenApp,
  fetchOMIM,
  getGeneByHpoIds,
  getHpoTerms,
  getTermByClinicalInfo,
  GetTermsByClinicalInfoDto,
  phenotypeExtract,
  phenotypeScoreSearch,
  translate,
  TranslateBulkDto,
  TranslateDto,
  translates,
  VariantDetailOmimData
} from '@/@core/services/app.service'
import { useMutation } from 'react-query'
import { copyToClipboard } from '@/@core/layouts/utils'
import ChinahpoSearch from '@/@core/components/chinahpo-search'

interface Column {
  id: string
  label: string
  minWidth?: number
  align?: 'right' | 'left' | 'center'
  format?: (value: any) => any
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

interface Item {
  HPO_id: string
  gene_names: string[]
}
interface RecordType {
  id: number
  gene: string
  hpo: any[]
  hpo_count: number

  // omimId: string
  omim?: string[]
}

interface TableParams {
  pagination?: TablePaginationConfig
  sortField?: SorterResult<any>['field']
  sortOrder?: SorterResult<any>['order']
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1]
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  },
  '& .MuiPaper-root': {
    maxWidth: 'none'
  }
}))

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box height="100%" sx={{ p: 3, pb: 0 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

// ** GLSanger Auth Utils
import { openGLSanger } from 'src/@core/utils/glsanger-auth'
import Index from '../analyze/Index'
import { SorterResult } from 'antd/es/table/interface'
import { NObject } from '@/@core/components/filter-variants'

const MonthlyOverview = (props: any) => {
  const chartInfo = props.samplesData
  const { t, i18n } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const router = require('next/router').useRouter()
  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState(0)
  const [search, setSearch] = React.useState<string>('')
  const [searchValue, setSearchValue] = React.useState<string>('')
  const [geneList, setGeneList] = React.useState<Item[]>([])
  const [gScoreList, setGScoreList] = React.useState<any[]>([])
  const chatClaudeRef = React.useRef<any>(null)
  const [clinicalInformation, setClinicalInformation] =
    React.useState<string>('')
  const [messageApi, contextHolder] = message.useMessage()
  const [termList, setTermList] = React.useState<any[]>([])
  const tblRef: Parameters<typeof Table>[0]['ref'] = React.useRef(null)
  const [backop, setBackop] = React.useState(false)

  const [totalData, setTotalData] = React.useState<RecordType[]>([])
  const [allData, setAllData] = React.useState<RecordType[]>([])
  const [data, setData] = React.useState<RecordType[]>([])
  const [loading, setLoading] = React.useState(false)
  const [tableParams, setTableParams] = React.useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10
    }
  })

  // ** Hook
  const theme = useTheme()

  const columns: TableProps<RecordType>['columns'] = [
    {
      title: <Typography>{t('gene')}</Typography>,
      dataIndex: 'gene',
      width: 100
    },
    {
      title: <Typography>{t('OMIM')}</Typography>,
      dataIndex: 'omim',
      width: 480,
      render: (value) => {
        return (
          <Typography
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'wrap'
            }}
          >
            {value?.join(';') || ''}
          </Typography>
        )
      }
    },
    {
      title: (
        <Tooltip title={t('0-100%，越高表型越匹配')}>
          <Typography>{t('Phenotypic matching value (G score)')}</Typography>
        </Tooltip>
      ),
      dataIndex: 'gScore',
      width: 120,
      render: (value) => {
        return (
          <Typography>
            {typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : ''}
          </Typography>
        )
      }
    },
    {
      title: <Typography>{`${t('Match HPO')}`}</Typography>,
      dataIndex: 'hpo_count',
      width: 80
    },
    {
      title: <Typography>{t('HPO')}</Typography>,
      dataIndex: 'hpo',
      render: (value) => {
        return (
          <Typography
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'wrap'
            }}
          >
            {value.map((item: any) => {
              return (
                <>
                  <span>{item.hpoid}</span>

                  <span style={{ color: 'red' }}>
                    {`(${i18n.language === 'zh' ? item.zh : item.en})`}
                  </span>
                </>
              )
            })}
          </Typography>
        )
      }
    }
  ]
  React.useEffect(() => {
    const data = processGeneHpoData(geneList)
    const newData = data
      .map((item) => {
        const hpoData = item.hpo || []

        const gScore =
          gScoreList.find((gScoreItem) => gScoreItem.gene === item.gene)
            ?.g_score || '.'
        return {
          ...item,
          gScore,
          hpo: hpoData.map((hpoid) => {
            const hpoItem = termList.find((term) => term.hpo_code === hpoid)
            return {
              title: hpoItem?.title || hpoid,
              hpoid: hpoItem?.hpo_code || hpoid,
              zh: hpoItem?.name_cn || '',
              en: hpoItem?.name_en || ''
            }
          })
        }
      })
      .filter((item) => {
        if (gScoreList && gScoreList.length > 0) {
          return item.gScore && item.gScore >= 0.5
        } else {
          return true
        }
      })
      .sort((a, b) => b.gScore - a.gScore)
    setTotalData(newData.slice(0, 50))
    setAllData(newData)
  }, [geneList, gScoreList, termList])

  const handleTableChange: TableProps<RecordType>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field
    })

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([])
    }
  }

  const fetchData = async () => {
    setLoading(true)
    let data = [...totalData]
    if (searchValue.trim() !== '') {
      const newData = totalData.filter((item) => {
        return (
          item.gene.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.omim
            ?.map((item) => item.toLowerCase())
            .includes(searchValue.toLowerCase()) ||
          item.hpo?.includes(searchValue.toLowerCase())
        )
      })
      data = newData
    }
    const { current = 1, pageSize = 10 } = tableParams.pagination || {}
    const arr = data.slice((current - 1) * pageSize, current * pageSize)
    const genes = arr.map((item) => item.gene)

    if (genes.length === 0) {
      setData([])
      setLoading(false)
      return
    }
    const OMIM = await getOMIM.mutateAsync({ geneNames: genes })
    const newData = arr.map((item) => {
      const omimData = OMIM.find((omimItem) => omimItem.geneName === item.gene)
      return {
        ...item,
        omimId: omimData?.geneMim || '',
        omim:
          omimData?.geneDiseaseTranslated?.data?.map((item) => {
            const inheritances: string[] = item.inheritance

            const inheritance = formatVariantInheritance(
              Array.from([...new Set(inheritances)]).join(',')
            )
            return `${item.disease}(${inheritance})`
          }) || []
      }
    })

    setLoading(false)
    setData(newData)
  }

  React.useEffect(() => {
    fetchData()
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    JSON.stringify(tableParams.filters),
    totalData,
    searchValue
  ])

  const getOMIM = useMutation({
    mutationFn: async (data: { geneNames: string[] }) =>
      await fetchOMIM({ geneNames: data.geneNames }),
    onSuccess: async (datas: VariantDetailOmimData[]) => {
      //post返回的结果
    }
  })

  const Translate = useMutation({
    mutationFn: async (data: TranslateDto) => await translate(data),
    onSuccess: (data, params, context) => {
      //post返回的结果
      // if (params.sourceLanguage === 'zh')
      //   GetTermByClinicalInfo.mutateAsync({
      //     clinicalInfo: data
      //   })
    }
  })

  const fetchGeneByHpoIds = useMutation({
    mutationFn: async (data: string[]) =>
      await getGeneByHpoIds({
        hpoIds: data
      }),
    onSuccess: (data) => {
      //

      if (data && Array.isArray(data) && data.length) {
        setGeneList(data)
      }
    }
  })

  const getPhenotypeExtractMutation = useMutation({
    mutationFn: async (data: { text: string; lang: string; format: string }) =>
      await phenotypeExtract(data),
    onSuccess: (data: { hpo: any[] }) => {
      //post返回的结果
      // refetch()
      setBackop(false)
    },
    onError: () => {
      setBackop(false)
    }
  })

  const getPhenotypeScoreMutation = useMutation({
    mutationFn: async (data: { hpo: string[] }) =>
      await phenotypeScoreSearch({
        hpo: data.hpo,
        topk: 0,
        blend: true
      }),
    onSuccess: (data: { ranked: any[] }) => {
      //post返回的结果
      // refetch()\
      if (data && Array.isArray(data.ranked)) setGScoreList(data.ranked)
    }
  })

  const getHpoGenes = (hpoIds: string[]) => {
    return fetchGeneByHpoIds.mutateAsync(hpoIds)
  }

  const translateValue = async (value: string[]) => {
    const sourceLanguage = 'en',
      targetLanguage = 'zh'

    const params: TranslateDto = {
      sourceLanguage,
      targetLanguage,
      sourceText: value.join('，')
    }

    const data = await Translate.mutateAsync(params)

    const flag = true
    if (flag) {
      // const data1 = await getHpoTermsMutation.mutateAsync(data)
      const data2 = await getPhenotypeExtractMutation.mutateAsync({
        text: data,
        lang: 'zh',
        format: 'legacy'
      })
      const parseData = data2.hpo ?? []
      if (parseData && Array.isArray(parseData)) {
        const newData = unique(parseData, 'name_en').map((i) => {
          return {
            ...i,
            title: `${i.hpo_code}${i.name_en}(${i.name_cn})`,
            keyId: `${i.hpo_code}${i.name_en.toLocaleLowerCase()}`
          }
        })

        const hpoIds = newData.map((item) => item.hpo_code)
        if (hpoIds.length) {
          getHpoGenes(hpoIds)
          getPhenotypeScoreMutation.mutateAsync({ hpo: hpoIds })
        }

        //

        setTermList(
          deduplicateArrays(unique([...newData], 'keyId'), termList)
            .filter((item: any) => item.name_en)
            .map((items: any) => {
              return {
                definition_cn: '',
                definition_en: '',
                hpo_code: items.hpo_code ?? '',
                name_cn: items.name_cn ?? '',
                name_en: items.name_en ?? '',
                alt_title: '',
                cn_title: '',
                incTitle: '',
                omim_code: 0,
                pre_title: items.name_en ?? '',
                prefix: '',
                title: `${items.hpo_code}${items.name_en}(${items.name_cn})`
              }
            })
        )
      }
      return
    }
    return []
  }

  const getHpoAndTerms = async () => {
    setBackop(true)

    message.info('查询中，请稍后')

    if (!containsChinese(clinicalInformation)) {
      //
      await translateValue(
        clinicalInformation.split(',').join('，').split('，')
      )
    } else {
      const flag = true
      if (flag) {
        // const data1 = await getHpoTermsMutation.mutateAsync(clinicalInformation)
        const data2 = await getPhenotypeExtractMutation.mutateAsync({
          text: clinicalInformation,
          lang: 'zh',
          format: 'legacy'
        })

        const parseData = data2.hpo ?? []
        if (parseData && Array.isArray(parseData)) {
          const newData = unique(parseData, 'name_en').map((i) => {
            return {
              ...i,
              title: `${i.hpo_code}${i.name_en}(${i.name_cn})`,
              keyId: `${i.hpo_code}${i.name_en.toLocaleLowerCase()}`
            }
          })

          const hpoIds = newData.map((item) => item.hpo_code)
          if (hpoIds.length) {
            getHpoGenes(hpoIds)
            getPhenotypeScoreMutation.mutateAsync({ hpo: hpoIds })
          }

          setTermList(
            deduplicateArrays(unique([...newData], 'keyId'), termList)
              .filter((item: any) => item.name_en)
              .map((items: any) => {
                return {
                  definition_cn: '',
                  definition_en: '',
                  hpo_code: items.hpo_code ?? '',
                  name_cn: items.name_cn ?? '',
                  name_en: items.name_en ?? '',
                  alt_title: '',
                  cn_title: '',
                  incTitle: '',
                  omim_code: 0,
                  pre_title: items.name_en ?? '',
                  prefix: '',
                  title: `${items.hpo_code}${items.name_en}(${items.name_cn})`
                }
              })
          )
        }
        return
      }
      return []
    }

    setBackop(false)
  }

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Card
      sx={{
        height: '100%'
      }}
    >
      {contextHolder}

      {/* <CardHeader
        title={t('Number of samples in the last 30 days')}
        titleTypographyProps={{
          sx: {
            letterSpacing: '0.15px !important',
            '::before': {
              content: '""',
              display: 'inline-block',
              width: 4,
              height: '1.6rem',
              bgcolor: 'primary.main',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: '-15px'
            },
            lineHeight: '1.6rem',
            position: 'relative',
            ml: '15px'
          }
        }}

        // action={
        //   <IconButton size='small' aria-label='settings' className='card-more-options' sx={{ color: 'text.secondary' }}>
        //     <DotsVertical />
        //   </IconButton>
        // }
      /> */}

      <CardContent
        sx={{ '& .apexcharts-xcrosshairs.apexcharts-active': { opacity: 0 } }}
      >
        <Typography
          variant="h6"
          sx={{
            '::before': {
              content: '""',
              display: 'inline-block',
              width: 4,
              height: '1.6rem',
              bgcolor: 'primary.main',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: '-15px'
            },
            lineHeight: '1.6rem',
            position: 'relative',
            ml: '15px'
          }}
        >
          {t('Toolbar')}
        </Typography>

        <Grid container spacing={4} mt={2}>
          <Grid item xs={12} md={6}>
            <Card
              onClick={() => {
                openGLSanger()
              }}
            >
              <CardActionArea>
                <CardContent>
                  <Stack direction="row" alignItems="center">
                    <Box>
                      <img
                        width={60}
                        src="/images/logos/sanger.png"
                        alt="bar-chart"
                      />
                    </Box>

                    <Typography>{t('GLSanger screenshot tool')}</Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              onClick={() => {
                chatClaudeRef.current && chatClaudeRef.current.handleClickOpen()
              }}
              sx={{
                height: '100%'
              }}
            >
              <CardActionArea
                sx={{
                  height: '100%'
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center">
                    <Box>
                      <SvgIcon>
                        <svg
                          width="60"
                          height="60"
                          viewBox="0 0 28 28"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.3041 4H13.6323L20.3282 20.9182H24L17.3041 4ZM6.69589 4L0 20.9182H3.7442L5.11351 17.3653H12.1187L13.488 20.9182H17.2322L10.5363 4H6.69589ZM6.32475 14.2233L8.6161 8.27765L10.9075 14.2233H6.32475Z"
                            fill="#181818"
                            width={'20px'}
                          />
                        </svg>
                      </SvgIcon>
                    </Box>

                    <Typography>{t('GLSAI Q&A tool')}</Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              onClick={() => {
                const w = window.open('/noetica', '_blank')
                if (w) w.opener = null
              }}
              sx={{
                height: '100%'
              }}
            >
              <CardActionArea
                sx={{
                  height: '100%'
                }}
              >
                <CardContent
                  sx={{
                    height: '100%'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Science sx={{ fontSize: 36, color: 'primary.main' }} />

                    <Typography>
                      {t('GLS AI Literature ACMG Evidence database')}
                    </Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              onClick={() => {
                handleClickOpen()
              }}
              sx={{
                height: '100%'
              }}
            >
              <CardActionArea
                sx={{
                  height: '100%'
                }}
              >
                <CardContent
                  sx={{
                    height: '100%'
                  }}
                >
                  <Stack direction="row" alignItems="center">
                    <Box>
                      <SwapHorizontal fontSize="large" />
                    </Box>

                    <Typography>{t('GLS HPO (AI)')}</Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'none'
          }}
        >
          <ChatClaude ref={chatClaudeRef} />
        </Box>

        <BootstrapDialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={open}
          sx={{
            '& .MuiPaper-root': {
              width: isWindow() ? 1600 : 'auto',
              minHeight: 200
            }
          }}
        >
          <DialogTitle>{t('GLS HPO (AI)')}</DialogTitle>

          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers>
            <Box>
              <Stack width={'100%'}>
                <Typography
                  sx={{
                    '::before': {
                      content: '""',
                      display: 'inline-block',
                      width: 4,
                      height: '1.25rem',
                      bgcolor: 'primary.main',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      left: '-10px'
                    },
                    lineHeight: '1.25em',
                    position: 'relative',
                    ml: '10px',
                    py: 2,
                    fontSize: 22
                  }}
                >
                  {t('Patient information')}:
                </Typography>

                <TextField
                  fullWidth
                  id="outlined-multiline-flexible"
                  multiline
                  maxRows={10}
                  minRows={4}
                  value={clinicalInformation}
                  onChange={(e) => setClinicalInformation(e.target.value)}
                  placeholder={t(
                    'Please enter Patient information and click the Apply button in the bottom right corner'
                  )}
                  sx={{
                    '& .MuiInputBase-input': {
                      '::placeholder': {
                        color: 'red'
                      }
                    }
                  }}
                />

                <Box textAlign={'right'} mt={2}>
                  <Button
                    startIcon={
                      backop ? (
                        <CircularProgress size="30px" />
                      ) : (
                        <PlusCircleOutline />
                      )
                    }
                    size="small"
                    variant="contained"
                    onClick={async () => {
                      if (clinicalInformation.trim() === '') {
                        messageApi.error('请输入临床信息')

                        return
                      }

                      await getHpoAndTerms()
                    }}
                    disabled={backop}
                  >
                    {t('One-click generation of phenotypes')}
                  </Button>
                </Box>

                <Box mt={4}>
                  <Tabs
                    value={value}
                    onChange={(e: any, newValue) => {
                      if (e.target.innerText === 'CNV Exon') {
                        //
                      }

                      setValue(newValue)
                    }}
                    aria-label="basic tabs example"
                    id={'header4'}
                  >
                    <Tab
                      sx={{
                        textTransform: 'capitalize'
                      }}
                      label={t('HPO List')}
                      {...a11yProps(0)}
                    />

                    <Tab
                      sx={{
                        textTransform: 'capitalize'
                      }}
                      label={t('HPO-associated genes')}
                      {...a11yProps(1)}
                    />
                  </Tabs>

                  <TabPanel value={value} index={0}>
                    <Box height={'100%'} maxWidth={'100%'}>
                      <Typography
                        sx={{
                          '::before': {
                            content: '""',
                            display: 'inline-block',
                            width: 4,
                            height: '1.25rem',
                            bgcolor: 'primary.main',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: '-10px'
                          },
                          lineHeight: '1.25em',
                          position: 'relative',
                          ml: '10px',
                          py: 2,
                          fontSize: 22
                        }}
                      >
                        {t('HPO List')}:
                      </Typography>

                      <ChinahpoSearch
                        termList={termList}
                        fn={(query: any) => {
                          setTermList(query)

                          return query
                        }}
                        title={t('HPO Revision')}
                        sx={{
                          flex: 1,
                          width: '100%',
                          '& .MuiInputBase-root': {
                            alignItems: 'flex-start'
                          },
                          '& .MuiInputBase-input': {
                            '::placeholder': {
                              color: 'red'
                            }
                          }
                        }}
                        type={'toolbar'}
                      />
                    </Box>
                  </TabPanel>

                  <TabPanel value={value} index={1}>
                    <Box height={'100%'} maxWidth={'100%'}>
                      <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                      >
                        <Stack
                          direction={'row'}
                          alignItems={'center'}
                          spacing={4}
                        >
                          <Typography
                            sx={{
                              '::before': {
                                content: '""',
                                display: 'inline-block',
                                width: 4,
                                height: '1.25rem',
                                bgcolor: 'primary.main',
                                position: 'absolute',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                left: '-10px'
                              },
                              lineHeight: '1.25em',
                              position: 'relative',
                              ml: '10px',
                              py: 2,
                              fontSize: 22
                            }}
                          >
                            {t('HPO-associated genes')}:
                          </Typography>

                          <Button
                            onClick={() => {
                              if (allData.length === 0) {
                                return message.error(
                                  t('No available data is available.')
                                )
                              }
                              const column = [
                                {
                                  title: t('gene'),
                                  value: 'gene'
                                },
                                {
                                  title: t('OMIM'),
                                  value: 'omimStr'
                                },
                                {
                                  title: t('OMIMID'),
                                  value: 'omimId'
                                },
                                {
                                  title: t(
                                    'Phenotypic matching value (G score)'
                                  ),
                                  value: 'gScoreValue'
                                },
                                {
                                  title: t('Match HPO'),
                                  value: 'hpo_count'
                                },
                                {
                                  title: t('HPO'),
                                  value: 'hpoStr'
                                }
                              ]

                              const arr = allData.map((item: any) => {
                                const obj: NObject = {}

                                column.map((i) => {
                                  if (i.value === 'hpoStr') {
                                    obj[i.title] = item['hpo']
                                      ? item['hpo']
                                          .map((item: any) => item.title)
                                          .join(', ')
                                      : ''
                                  } else if (i.value === 'omimStr') {
                                    obj[i.title] = item['omim']
                                      ? item['omim'].join(', ')
                                      : ''
                                  } else if (i.value === 'gScoreValue') {
                                    obj[i.title] =
                                      typeof item['gScore'] === 'number' &&
                                      item['gScore']
                                        ? `${(item['gScore'] * 100).toFixed(
                                            2
                                          )}%`
                                        : ''
                                  } else {
                                    obj[i.title] = item[i.value]
                                  }
                                })

                                return obj
                              })

                              // 导出XLSX
                              const worksheet1 = XLSX.utils.json_to_sheet(arr)
                              const workbook = XLSX.utils.book_new()

                              XLSX.utils.book_append_sheet(
                                workbook,
                                worksheet1,
                                'Sheet1'
                              )
                              messageApi.success(t('Export success'))

                              XLSX.writeFile(
                                workbook,
                                `HPO-${formatDate(
                                  new Date(),
                                  'YYYY-MM-DD'
                                )}.xlsx`
                              )
                            }}
                            startIcon={<Download />}
                            variant="contained"
                          >
                            {t('download')}
                          </Button>
                        </Stack>

                        <Paper
                          component="form"
                          sx={{
                            p: '2px 4px',
                            display: 'flex',
                            alignItems: 'center',
                            maxWidth: 400,
                            height: '2rem',
                            width: '400px !important',
                            minHeight: '2rem !important'
                          }}
                        >
                          <InputBase
                            sx={{ ml: 1, flex: 1 }}
                            placeholder={`${t('Search')} ${t('gene')}`}
                            inputProps={{ 'aria-label': 'search' }}
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()

                                setSearchValue(search)
                              }
                            }}
                            onFocus={() => {
                              //
                            }}
                          />

                          <IconButton
                            type="button"
                            sx={{ p: '10px' }}
                            aria-label="search"
                            onClick={() => {
                              setSearchValue(search)
                            }}
                          >
                            <SearchIcon />
                          </IconButton>
                        </Paper>
                      </Stack>

                      <Stack
                        pt={2}
                        maxWidth={'100%'}
                        maxHeight={600}
                        spacing={4}
                        sx={{
                          '& .MuiPaper-root': {
                            width: isWindow() ? '1100px' : 'auto',
                            minHeight: 200
                          }
                        }}
                      >
                        <Table<RecordType>
                          columns={columns}
                          rowKey="id"
                          dataSource={data}
                          pagination={{
                            ...tableParams.pagination,
                            total:
                              searchValue.trim() === ''
                                ? totalData.length
                                : data.length
                          }}
                          ref={tblRef}
                          loading={loading}
                          onChange={handleTableChange}
                          scroll={{ y: 400 }}
                        />
                      </Stack>
                    </Box>
                  </TabPanel>
                </Box>

                <Box textAlign={'right'} mt={2}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      ml: 2
                    }}
                    onClick={() => {
                      setTermList([])

                      setClinicalInformation('')

                      setGeneList([])
                    }}
                  >
                    {t('Clear')}
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      ml: 2
                    }}
                    onClick={(e) => {
                      if (termList.length > 0) {
                        copyToClipboard(
                          termList
                            .map(
                              (item) =>
                                `${item.hpo_code}(${
                                  i18n.language === 'zh'
                                    ? item.name_cn
                                    : item.name_en
                                })`
                            )
                            .join('，'),
                          e
                        )

                        messageApi.success(t('Successful copy'))
                      }
                    }}
                  >
                    {t('Copy')}
                  </Button>
                </Box>
              </Stack>

              {/* <Stack py={4}>
                <Box maxHeight={400} overflow="auto">
                  <Stack
                    flexWrap="wrap"
                    direction={'row'}
                    spacing={2}
                    useFlexGap
                  >
                    {ChatQwen.map((item, index) => (
                      <Chip
                        label={`${item.hpo_code}(${
                          i18n.language === 'zh' ? item.name_cn : item.name_en
                        })`}
                        color="primary"
                        key={index}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack> */}
            </Box>
          </DialogContent>
        </BootstrapDialog>

        {/* <ReactApexcharts
          type="bar"
          height={220}
          options={options}
          series={[
            {
              data: [
                {
                  x: 'WES',
                  y: +chartInfo?.wes_month
                },
                {
                  x: 'WGS',
                  y: +chartInfo?.wgs_month
                },
                {
                  x: 'RNA',
                  y: +chartInfo?.rna_month
                },
                {
                  x: 'MicroArray',
                  y: +chartInfo?.microarray_month
                },
                {
                  x: 'Panel',
                  y: +chartInfo?.gene_panel_month
                }
              ]
            }
          ]}
        /> */}

        {/* <Box sx={{ mb: 7, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ mr: 4 }}>
            45%
          </Typography>

          <Typography variant="body2">
            Your sales performance is 45% 😎 better compared to last month
          </Typography>
        </Box> */}

        {/* <Button fullWidth variant="contained">
          Details
        </Button> */}
      </CardContent>
    </Card>
  )
}

export default MonthlyOverview
